/* The maturity scoring methodology, as data.
 *
 * Transcribed from `docs/maturity-scoring-methodology.md`, which is the source
 * of record. When the methodology changes, that document changes first and this
 * file follows it; the two are checked against each other by reading, because a
 * scale is prose and prose does not have a test.
 *
 * It lives in the frontend rather than behind the API because it is reference
 * material shown to a reader, not vocabulary the database validates against.
 * Nothing here can make a write succeed or fail. The seven keys must match the
 * dimension columns in `app/model.py`, and that is the only coupling.
 */

export interface Anchor {
  score: number;
  level: string;
  rung?: string;
  test: string;
}

export interface DimensionDoc {
  question: string;
  /* What this dimension measures, and where its boundary with the neighbouring
     dimension runs. The boundaries are the point: without them two dimensions
     claim the same evidence and the average double-counts it. */
  measures: string;
  componentsLabel?: string;
  components?: { name: string; text: string }[];
  anchors: Anchor[];
  notes?: string[];
}

/* The generic scale every dimension is scored against. Intermediate values (40,
   70) mean "past this anchor, short of the next" and should never be used to
   avoid a judgement. */
export const GENERIC_SCALE: { score: number; meaning: string }[] = [
  { score: 0, meaning: "Does not exist" },
  { score: 10, meaning: "Exists as belief or awareness only" },
  { score: 30, meaning: "Exists partially, or exists but unverified" },
  { score: 60, meaning: "Complete and verified internally" },
  { score: 80, meaning: "Proven externally, once" },
  { score: 100, meaning: "Proven repeatedly, and current" },
];

export const SCORING: Record<string, DimensionDoc> = {
  strategy: {
    question: "Is the offering defined — who it is for, what it promises, why us?",
    measures:
      "Measures the quality and completeness of the definition. Whether the market agreed is a separate dimension.",
    componentsLabel: "The seven elements of a complete strategic concept",
    components: [
      { name: "Buyer", text: "Role, company profile, and the trigger that makes them look" },
      { name: "Problem", text: "The symptom, in their words" },
      { name: "Promise", text: "What they get, and what changes as a result" },
      { name: "Why us", text: "The right to win, and why it is not trivially copyable" },
      { name: "Scope and shape", text: "What is in, what is out, which engagement depths" },
      { name: "Portfolio fit", text: "Relationship to other services, what it leads to next" },
      { name: "Commercial model", text: "How it is sold and priced — the shape, not the number" },
    ],
    anchors: [
      { score: 0, level: "Nothing", test: "Not defined. May not have been discussed." },
      {
        score: 10,
        level: "Hypothesis",
        test: "Someone believes there is an offering here. Verbal only, nothing written.",
      },
      {
        score: 30,
        level: "Partial concept",
        test: "Written down, but fewer than seven elements answered, or answered with placeholders.",
      },
      {
        score: 60,
        level: "Complete definition",
        test: "All seven answered well enough that a colleague could act on them without asking the author.",
      },
      {
        score: 80,
        level: "Approved",
        test: "A named decision-maker has signed it off. It is the version of record — marketing, sales and delivery build from it rather than their own interpretations.",
      },
      {
        score: 100,
        level: "Current",
        test: "Reviewed against reality at least once since approval, and either confirmed or updated.",
      },
    ],
  },

  core_capabilities: {
    question: "Do we have the people, tooling and track record to deliver it?",
    measures:
      "Measures competence — whether we can do it. Delivery Readiness asks the different question of whether it is packaged so a team can execute it.",
    componentsLabel: "Three components",
    components: [
      { name: "People", text: "Skill and availability" },
      { name: "Tooling", text: "Reusable technical assets" },
      { name: "Track record", text: "Having actually done it" },
    ],
    anchors: [
      {
        score: 0,
        level: "None",
        test: "No relevant skill, no tooling, nobody has done anything like it.",
      },
      {
        score: 10,
        level: "Adjacent",
        test: "Related skills exist somewhere in the company. Nobody has been asked whether they could deliver this.",
      },
      {
        score: 30,
        level: "Identified",
        test: "We know who would deliver it and what they would need. Gaps are named. No assets exist yet.",
      },
      {
        score: 60,
        level: "Ready in principle",
        test: "Named people are available and competent, and reusable assets exist — templates, configurations, reference implementations. Proven as internal practice. Never sold in this shape.",
      },
      {
        score: 80,
        level: "Proven once",
        test: "Delivered at least once, as scoped, to a real client, at acceptable quality.",
      },
      {
        score: 100,
        level: "Repeatable",
        test: "Delivered by more than one team or lead, with consistent quality. Not dependent on a single person.",
      },
    ],
    notes: [
      "The internal-versus-scoped distinction. Practice that runs inside our own engagements counts up to 60. A client buying this shape and us delivering it is required for 80 and above. This is what stops an existing internal practice scoring falsely high the day it is named as a service.",
    ],
  },

  delivery_readiness: {
    question: "Could a team start on Monday — playbook, estimates, staffing?",
    measures:
      "Measures transferability — whether the knowledge has left the person who holds it. A brilliant architect who could deliver tomorrow but has written nothing down scores high on Core Capabilities and near zero here.",
    componentsLabel: "Four artifacts",
    components: [
      { name: "Playbook", text: "The steps, in order, with the artifacts each produces" },
      { name: "Estimates", text: "Effort and duration, with the variables that move them" },
      { name: "Staffing model", text: "Which roles, how many, at what seniority" },
      {
        name: "Entry and exit criteria",
        text: "What must be true to start, what “done” looks like",
      },
    ],
    anchors: [
      {
        score: 0,
        level: "Nothing",
        test: "No playbook, no estimates, no staffing model. Each engagement would be improvised.",
      },
      {
        score: 10,
        level: "In someone's head",
        test: "One person could run it. Nothing written. If they are unavailable, it does not happen.",
      },
      {
        score: 30,
        level: "Sketched",
        test: "Some of the four exist, usually a rough sequence of steps. Not specific enough to hand over.",
      },
      {
        score: 60,
        level: "Documented",
        test: "All four exist and are written down. A team could start with a briefing from the author.",
      },
      {
        score: 80,
        level: "Handover-ready",
        test: "A team that has not delivered it before could start from the artifacts alone, without access to the author.",
      },
      {
        score: 100,
        level: "Proven and refined",
        test: "Executed by a team working from the artifacts, and the artifacts were updated from what that run revealed.",
      },
    ],
  },

  marketing_assets: {
    question: "Website pages and collateral, published and on-message.",
    measures:
      "On-message is a gate, not a component. Below 60 the score measures how much exists. At 60 and above, existence stops counting unless the content matches the approved strategy — which means this dimension cannot meaningfully exceed 30 while Strategy sits below 60.",
    componentsLabel: "Four asset types",
    components: [
      { name: "Core page", text: "Findable, with a working CTA" },
      { name: "Proof", text: "Evidence a buyer would accept" },
      { name: "Collateral", text: "Something a prospect can be sent" },
      { name: "Discoverability", text: "Search terms, internal linking, entry points" },
    ],
    anchors: [
      { score: 0, level: "Nothing", test: "The service is invisible outside the company." },
      {
        score: 10,
        level: "Mentioned",
        test: "Appears in a list or a line on another page. No destination of its own.",
      },
      {
        score: 30,
        level: "Published",
        test: "A page exists. Content predates or diverges from the approved strategy, or the page is incomplete — dead CTAs, no proof, no route in.",
      },
      {
        score: 60,
        level: "On-message",
        test: "Page reflects the approved strategy. Complete and working: live CTA, at least one piece of proof, reachable from where a buyer would look.",
      },
      {
        score: 80,
        level: "Full set",
        test: "Core page plus collateral plus discoverability. Sales can send something without asking marketing to make it.",
      },
      {
        score: 100,
        level: "Performing",
        test: "The assets demonstrably work — traffic, engagement, inbound that names the service. Reviewed and kept current.",
      },
    ],
    notes: [
      "A score of 30 is ambiguous between “early” and “published and actively misleading.” Those need different urgency, so the distinction belongs in the evidence note rather than the number.",
    ],
  },

  sales_enablement: {
    question: "Can Sales pitch it — pricing, objections, qualifying questions?",
    measures:
      "Commercial readiness of the selling team. Presales Training covers the adjacent technical question of whether they can scope it.",
    componentsLabel: "Five components",
    components: [
      { name: "Qualification", text: "Who this is for, and the disqualifying signals" },
      { name: "Pitch", text: "The narrative and materials" },
      { name: "Pricing", text: "What it costs, what moves the number, discretion limits" },
      { name: "Objections", text: "The four or five that actually come up, with answers" },
      { name: "Handoff", text: "When to bring in presales or delivery, and with what" },
    ],
    anchors: [
      {
        score: 0,
        level: "Nothing",
        test: "Sales cannot describe the service. It will not be pitched.",
      },
      {
        score: 10,
        level: "Aware",
        test: "Sales knows it exists and roughly what it does. No materials, no price.",
      },
      {
        score: 30,
        level: "Partial",
        test: "Some of the five exist, usually a deck. No price, or no qualification criteria. A rep would improvise.",
      },
      {
        score: 60,
        level: "Equipped",
        test: "All five exist and have been shared. A rep who was not involved can pitch it after reading them.",
      },
      {
        score: 80,
        level: "Demonstrated",
        test: "A rep outside the build team has pitched it in a live conversation and handled it competently.",
      },
      {
        score: 100,
        level: "In the motion",
        test: "Reps raise it unprompted with the right prospects. It appears in pipeline without anyone reminding them.",
      },
    ],
    notes: [
      "Nothing reaches 60 without a price, which makes this dimension dependent on element 7 of Strategy.",
      "The 100 is the useful one. Reps reaching for a service voluntarily is a strong signal that arrives months before revenue — and a service sales will not pitch without a nudge is failing internally in a way that can look like market failure.",
    ],
  },

  market_validation: {
    question: "Has the market said yes, ideally by paying for it?",
    measures:
      "External evidence of demand, scored against a six-rung ladder: does anyone arrive · do they recognise the problem · do they want this as the answer · does it survive a price · can we deliver it profitably with value delivered · does it lead to the next sale.",
    anchors: [
      {
        score: 0,
        level: "Untested",
        rung: "—",
        test: "Nobody outside the company has been asked.",
      },
      {
        score: 10,
        level: "Assumed",
        rung: "—",
        test: "Belief based on our own reading of the market. Anecdote, not evidence.",
      },
      {
        score: 30,
        level: "Problem confirmed",
        rung: "1–2",
        test: "Prospects who did not hear it from us describe the problem in recognisable terms.",
      },
      {
        score: 60,
        level: "Offer wanted",
        rung: "3–4",
        test: "Prospects want this specific answer and engage with a price — proposals, negotiations, or a sale attached to a larger deal. Not yet bought standalone.",
      },
      {
        score: 80,
        level: "Bought standalone",
        rung: "4–5",
        test: "At least one client has paid for it on its own, and got the value promised.",
      },
      {
        score: 100,
        level: "Repeating",
        rung: "5–6",
        test: "Multiple standalone sales to different clients, delivered profitably, leading to further work. Not dependent on one champion or one relationship.",
      },
    ],
    notes: [
      "Selling once is not the top of this scale. It proves rung 4 and says nothing about whether the client got value, whether we made margin, or whether it repeats.",
      "A service stuck at 60 — wanted, discussed, only ever sold attached to a larger engagement — is telling you it is a phase rather than a standalone offering. And buyer research can reach 30 but no further: stated willingness to pay is not evidence of demand.",
      "For free diagnostics, rungs 4 to 6 are read as conversion rather than sale — does it convert to a paid conversation, to a paid engagement, and at what sustained rate.",
    ],
  },

  presales_training: {
    question: "Have Presales and client-facing Delivery been trained on it?",
    measures:
      "Technical readiness of the people who face clients before a contract exists — presales engineers, solution architects, and delivery leads who join sales conversations.",
    componentsLabel: "Four components",
    components: [
      {
        name: "Technical story",
        text: "What it is, how it works, how it differs from adjacent things",
      },
      { name: "Scoping", text: "Sizing an engagement from what a prospect describes" },
      { name: "Hard questions", text: "The technical objections and the honest answers" },
      { name: "Boundaries", text: "What we do not do, and when to say the fit is wrong" },
    ],
    anchors: [
      {
        score: 0,
        level: "Untrained",
        test: "Presales could not hold a technical conversation about it.",
      },
      {
        score: 10,
        level: "Informed",
        test: "Told it exists. No session, no materials, no practice.",
      },
      {
        score: 30,
        level: "Briefed",
        test: "A session has been run, or materials circulated. No check that anything landed.",
      },
      {
        score: 60,
        level: "Trained",
        test: "Presales can explain it and scope it correctly in a rehearsal or internal review. Verified, not assumed.",
      },
      {
        score: 80,
        level: "Practised",
        test: "Someone outside the build team has handled a real client technical conversation on it competently.",
      },
      {
        score: 100,
        level: "Bench depth",
        test: "More than one person can do this reliably, without the service's originator in the room.",
      },
    ],
    notes: [
      "Delivering a service repeatedly produces knowledge, not training. Knowledge that has not been transferred and verified scores 10. This dimension is also gated by Delivery Readiness — training cannot teach a playbook that does not exist.",
    ],
  },
};

/* ── colour bands ──────────────────────────────────────────────────────────
 *
 * The anchors are the bands. A score of 40 is "past 30, short of 60", so it is
 * coloured as 30 — which is the whole point of anchoring: the colour says which
 * rung the thing has actually cleared, not how close it looks to the next one.
 *
 * Only an exact 100 is dark green. Repeatedly proven and current is a different
 * claim from proven once, and a ramp that made 95 look like 100 would erase it.
 *
 * The colours themselves are CSS variables, so light and dark mode are the
 * theme's problem rather than this module's.
 */
export const BANDS: number[] = [0, 10, 30, 60, 80, 100];

export function bandFor(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  let band = BANDS[0];
  for (const anchor of BANDS) if (value >= anchor) band = anchor;
  return band;
}
